import dayjs, { Dayjs } from "dayjs";
import { EMPTY_STR } from "@/constants/string";

export const JOB_FILTER_COMPANY_ARCHIVED = "company_archived";

export type TJobFilterFormExtended = {
  status: string[];
  tagIds: string[];
  managementNumber: string;
  companyName: string[];
  jobName: string;
  recruiterId: string[];
  companySpecificIndustryId: string[];
  japaneseLevelType: string[];
  recruitmentNumber: number | string;
  workplace: string;
  recruitmentLocationType: string[];
  registrationDateFrom: Dayjs | null;
  registrationDateTo: Dayjs | null;
};

export type TJobDisplayColumnSetting = {
  id: string;
  label: string;
  visible: boolean;
  order: number;
};

export const FILTER_LABEL_MAX_LENGTH = 30;
export const FILTER_LABEL_FULL_WIDTH_MAX_LENGTH = 60;

export const defaultJobFilterValues: TJobFilterFormExtended = {
  status: [],
  tagIds: [],
  managementNumber: "",
  companyName: [],
  jobName: "",
  recruiterId: [],
  companySpecificIndustryId: [],
  japaneseLevelType: [],
  recruitmentNumber: "",
  workplace: "",
  recruitmentLocationType: [],
  registrationDateFrom: null,
  registrationDateTo: null,
};

export const defaultJobDisplayColumns: TJobDisplayColumnSetting[] = [
  { id: "status", label: "ステータス", visible: true, order: 1 },
  { id: "tag", label: "タグ", visible: true, order: 2 },
  { id: "managementNumber", label: "求人管理番号", visible: true, order: 3 },
  { id: "companyName", label: "企業名", visible: true, order: 4 },
  { id: "jobName", label: "求人タイトル", visible: true, order: 5 },
  { id: "recruiterId", label: "求人担当者", visible: true, order: 6 },
  { id: "companySpecificIndustryId", label: "分野", visible: true, order: 7 },
  { id: "japaneseLevelType", label: "必要な日本語レベル", visible: true, order: 8 },
  { id: "recruitmentNumber", label: "募集人数", visible: true, order: 9 },
  { id: "workplace", label: "勤務場所", visible: true, order: 10 },
  { id: "recruitmentLocationType", label: "募集地", visible: true, order: 11 },
  { id: "createdAt", label: "登録日", visible: true, order: 12 },
];

// Normalize date fields from strings (JSON) to Dayjs objects for DatePicker compatibility
export const normalizeJobDateFields = (filters: Partial<TJobFilterFormExtended>): Partial<TJobFilterFormExtended> => {
  const result = { ...filters };
  if (result.registrationDateFrom && typeof result.registrationDateFrom === "string") {
    const day = dayjs(result.registrationDateFrom);
    result.registrationDateFrom = day.isValid() ? day : null;
  }
  if (result.registrationDateTo && typeof result.registrationDateTo === "string") {
    const day = dayjs(result.registrationDateTo);
    result.registrationDateTo = day.isValid() ? day : null;
  }
  return result;
};

// Serialize title + filters for dirty-checking comparison
export const serializeJobForComparison = (title: string, filters: TJobFilterFormExtended): string => {
  const normalized = { ...filters };
  if (normalized.registrationDateFrom) {
    normalized.registrationDateFrom = dayjs(normalized.registrationDateFrom).format(
      "YYYY-MM-DD",
    ) as unknown as typeof normalized.registrationDateFrom;
  }
  if (normalized.registrationDateTo) {
    normalized.registrationDateTo = dayjs(normalized.registrationDateTo).format("YYYY-MM-DD") as unknown as typeof normalized.registrationDateTo;
  }
  return JSON.stringify({ title, filters: normalized });
};

export const JOB_FILTER_FIELD_LABELS: { key: keyof TJobFilterFormExtended; label: string }[] = [
  { key: "status", label: "ステータス" },
  { key: "tagIds", label: "タグ" },
  { key: "managementNumber", label: "求人管理番号" },
  { key: "companyName", label: "企業名" },
  { key: "jobName", label: "求人タイトル" },
  { key: "recruiterId", label: "求人担当者" },
  { key: "companySpecificIndustryId", label: "分野" },
  { key: "japaneseLevelType", label: "必要な日本語レベル" },
  { key: "recruitmentNumber", label: "募集人数" },
  { key: "workplace", label: "勤務場所" },
  { key: "recruitmentLocationType", label: "募集地" },
];

export const JOB_DATE_RANGE_FIELD = {
  fromKey: "registrationDateFrom" as const,
  toKey: "registrationDateTo" as const,
  label: "登録日",
};

/** Check if a filter value is empty (empty string, null, undefined, or empty array) */
export const isEmptyJobFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const stripEmptyJobFilterFields = (filters: TJobFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyJobFilterValue(value)) continue;
    result[key] = value;
  }
  return result;
};

/** Multi-select filter field keys */
export const JOB_MULTI_SELECT_FILTER_KEYS: (keyof TJobFilterFormExtended)[] = [
  "status",
  "tagIds",
  "companyName",
  "companySpecificIndustryId",
  "recruiterId",
  "japaneseLevelType",
  "recruitmentLocationType",
];

/** Normalize legacy scalar filter values to arrays for backward compatibility with saved conditions */
export const normalizeJobMultiSelectFields = (filters: Partial<Record<string, unknown>>): Partial<TJobFilterFormExtended> => {
  const result = { ...filters };
  for (const key of JOB_MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      // tagIds are string ids (tags.id is bigint); coerce any numeric elements from persisted JSON to string
      if (key === "tagIds") result[key] = (value as unknown[]).map(String);
      continue;
    }
    if (value === "" || value === null) {
      result[key] = [];
    } else if (key === "tagIds") {
      const n = Number(value);
      result[key] = isNaN(n) ? [] : [n];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TJobFilterFormExtended>;
};

export const formatJobDateValue = (value: Dayjs | string | null): string => {
  if (!value) return EMPTY_STR.DATE;
  const day = dayjs(value);
  return day.isValid() ? day.format("YYYY/MM/DD") : EMPTY_STR.DATE;
};

export const JOB_LOCKED_COLUMN_IDS = ["status", "managementNumber"];
